// Tiny dependency-free Web Audio synth — no .mp3/.wav assets, no licensing
// concerns, fits the retro arcade tone. Every export degrades to a silent
// no-op if AudioContext or localStorage aren't available (older browsers,
// the vitest `node` test environment), rather than throwing.

import { getAudioContext, tone } from "./audioCore.js";

const STORAGE_KEY = "law:soundMuted";

function readStoredMuted() {
  if (typeof localStorage === "undefined") return false;
  try {
    return localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

let muted = readStoredMuted();

export function isSoundMuted() {
  return muted;
}

export function setSoundMuted(value) {
  muted = Boolean(value);
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.setItem(STORAGE_KEY, muted ? "1" : "0");
    } catch {
      // Private browsing / storage quota — muting still works for this tab.
    }
  }
  return muted;
}

export function toggleSoundMuted() {
  return setSoundMuted(!muted);
}

const SOUNDS = {
  success: (ctx, t) => {
    tone(ctx, { freq: 523.25, start: t, duration: 0.14 });
    tone(ctx, { freq: 659.25, start: t + 0.12, duration: 0.22 });
  },
  levelup: (ctx, t) => {
    [523.25, 659.25, 783.99, 1046.5].forEach((freq, i) => {
      tone(ctx, { freq, start: t + i * 0.1, duration: 0.18 });
    });
  },
  coin: (ctx, t) => {
    tone(ctx, { freq: 988, start: t, duration: 0.08 });
    tone(ctx, { freq: 1318.5, start: t + 0.07, duration: 0.14 });
  },
  error: (ctx, t) => {
    tone(ctx, { freq: 220, start: t, duration: 0.2, type: "sawtooth", gain: 0.12 });
    tone(ctx, { freq: 174.6, start: t + 0.12, duration: 0.25, type: "sawtooth", gain: 0.12 });
  },
  challenge: (ctx, t) => {
    tone(ctx, { freq: 740, start: t, duration: 0.1, type: "triangle" });
    tone(ctx, { freq: 987.77, start: t + 0.1, duration: 0.1, type: "triangle" });
    tone(ctx, { freq: 1318.5, start: t + 0.2, duration: 0.18, type: "triangle" });
  },
};

export function playSound(name) {
  if (muted) return;
  const ctx = getAudioContext();
  if (!ctx) return;
  const play = SOUNDS[name];
  if (!play) return;
  try {
    play(ctx, ctx.currentTime);
  } catch {
    // Audio is a nice-to-have — never let a synth failure break gameplay.
  }
}
